import React, { useCallback, useMemo } from 'react';
import { SimpleDashboardContent } from 'features/dashboard/DashboardContent';
import { DashboardHeader } from 'features/dashboard/dashboard-header/DashboardHeader';
import { Column, TableAction } from 'components/display/Table/types';
import { Building2, Crown, Landmark } from 'lucide-react';
import { GenericViewHeader } from 'back-office/shared/components/GenericViewHeader';
import ServerTable from 'shared/contexts/table-context/components/ServerTable';
import {
  FacetQueryCommand,
  INCLUDE_TESTS_BOOLEAN_FACET,
  mapFacetFiltersToFiltersDto
} from 'shared/contexts/table-context/facetFilters';
import { buildExportCommand, exportListCsv } from 'shared/contexts/table-context/exportCsv';
import { OnCreateConfig } from 'shared/contexts/table-context/components/types';
import { courtiersAPI } from 'services/swagger-api';
import { CourtierListItem } from 'api/courtiers';
import { TableResponse } from 'slices/generic-table/genericTableSlice';
import { formatInstant, formatInstantDay } from 'core/utils/dates';

interface CourtiersViewProps {
  /** Liste admin BO (tous les courtiers) : routes /admin/liste. */
  admin?: boolean;
  /** Action de création (ex. « Ajouter un courtier »), fournie par l'écran admin. */
  createConfig?: OnCreateConfig;
  /** Filtres imposés (ex. { plateformeIds: [orgId] }) — fusionnés au filtre facettes. */
  forcedFilters?: Record<string, any>;
  /** Actions de ligne (ex. voir / retirer de la plateforme). */
  rowActions?: TableAction<CourtierListItem>[];
  /** Colonnes masquées par défaut (contexte embarqué : n'afficher que l'essentiel). */
  defaultHiddenColumns?: string[];
  /** Clé de cache des préférences colonnes (défaut selon le scope). */
  storageKey?: string;
  /** Incrémenter pour forcer un re-fetch (ex. après retrait d'un courtier). */
  refreshTrigger?: number;
  /** Embarqué dans une autre page : rend seulement le tableau (sans layout/dashboard). */
  embedded?: boolean;
  /** Titre du tableau en mode embarqué (ex. « Intermédiaires de la plateforme »). */
  titleLabel?: string;
  /** Description affichée sous le titre en mode embarqué. */
  titleDescription?: string;
  /** Facettes à masquer dans ce contexte (ex. « plateformes » sur la page d'une orga). */
  hiddenFacetKeys?: string[];
  /** Contexte plateforme : badge la ligne du cabinet titulaire (« Gère la
   * plateforme », ou « Représente la plateforme » en mode Administrée). */
  gouvernante?: { personneMoraleId: string; estAdministree: boolean };
}

// createdAt = INSTANT (Z) : converti au fuseau du navigateur
const fmtDate = (iso?: string | Date | null): string => formatInstantDay(iso);

const fmtEur = (amount: number): string =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);

/** Dernière connexion : date + heure, ou « Jamais connecté » si absent. */
const fmtLastLogin = (iso?: string | Date | null): React.ReactNode =>
  iso ? (
    formatInstant(iso)
  ) : (
    <span className="italic text-gray-400">Jamais connecté</span>
  );

export const CourtiersView: React.FC<CourtiersViewProps> = ({
  admin = false,
  createConfig,
  forcedFilters,
  rowActions,
  defaultHiddenColumns,
  storageKey,
  refreshTrigger,
  embedded = false,
  titleLabel,
  titleDescription,
  hiddenFacetKeys,
  gouvernante
}) => {
  const columns: Column<CourtierListItem>[] = useMemo(
    () => [
      {
        key: 'name',
        label: 'Raison sociale',
        sortKey: 'name',
        render: (value, row) => (
          <span className="flex items-center gap-2 font-semibold text-gray-900">
            {value as string}
            {gouvernante && row.personneMoraleId === gouvernante.personneMoraleId &&
              (gouvernante.estAdministree ? (
                <span
                  title="Plateforme administrée : le back-office pilote, ce cabinet la représente sans la gérer."
                  className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 px-2.5 py-0.5 text-[11px] font-semibold text-indigo-700"
                >
                  <Landmark size={12} />
                  Représente la plateforme
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-100 px-2.5 py-0.5 text-[11px] font-semibold text-amber-800">
                  <Crown size={12} className="fill-amber-400 text-amber-600" />
                  Gère la plateforme
                </span>
              ))}
          </span>
        )
      },
      {
        key: 'email',
        label: 'Contact',
        sortKey: 'phone',
        render: (value, row) => (
          <div className="text-sm leading-tight text-gray-700">
            <div>{(value as string) || '—'}</div>
            {row.phone && <div className="text-xs text-gray-500">{row.phone}</div>}
          </div>
        )
      },
      {
        key: 'siret',
        label: 'SIRET',
        render: (value) => (
          <span className="font-mono text-xs text-gray-600">{(value as string) || '—'}</span>
        )
      },
      {
        key: 'orias',
        label: 'N° ORIAS',
        sortKey: 'orias',
        render: (value) => (
          <span className="font-mono text-xs text-gray-600">{(value as string) || '—'}</span>
        )
      },
      {
        key: 'category',
        label: 'Catégorie',
        sortKey: 'category',
        render: (value) => <span className="text-sm text-gray-700">{(value as string) || '—'}</span>
      },
      {
        key: 'contactName',
        label: 'Contact principal',
        render: (value) => <span className="text-sm text-gray-700">{(value as string) || '—'}</span>
      },
      {
        // « Jamais connecté » si absent (le back gère le tri des valeurs absentes)
        key: 'lastLoginAt',
        label: 'Dernière connexion',
        sortKey: 'lastLoginAt',
        render: (value) => <span className="text-sm text-gray-700">{fmtLastLogin(value as string)}</span>
      },
      {
        key: 'createdAt',
        label: 'Créé le',
        sortKey: 'date',
        render: (value) => <span className="text-sm text-gray-700">{fmtDate(value as string)}</span>
      },
      {
        key: 'simulationCount',
        label: 'Simulations',
        sortKey: 'simulationCount',
        render: (value) => <span className="text-sm text-gray-700">{value as number}</span>
      },
      {
        key: 'quoteCount',
        label: 'Devis',
        sortKey: 'quoteCount',
        render: (value) => <span className="text-sm text-gray-700">{value as number}</span>
      },
      {
        key: 'contractCount',
        label: 'Contrats',
        sortKey: 'contractCount',
        render: (value) => <span className="text-sm text-gray-700">{value as number}</span>
      },
      {
        key: 'totalPremium',
        label: 'Primes',
        sortKey: 'totalPremium',
        render: (value) => (
          <span
            className="text-sm font-medium tabular-nums text-gray-700"
            title="Somme des primes quittancées des contrats en cours"
          >
            {fmtEur((value as number) ?? 0)}
          </span>
        )
      },
      {
        key: 'clientCount',
        label: 'Clients',
        sortKey: 'clientCount',
        render: (value) => <span className="text-sm text-gray-700">{value as number}</span>
      }
    ],
    [gouvernante]
  );

  const fetchCourtiers = useCallback(
    async (params: any): Promise<TableResponse<CourtierListItem>> => {
      const command = {
        pageNumber: params.pageNumber,
        pageSize: params.pageSize,
        searchString: params.searchString || undefined,
        sortBy: params.sortBy,
        sortDirection: params.sortDirection,
        filters: mapFacetFiltersToFiltersDto(params.filters)
      };
      // Filtres imposés (ex. plateformeIds de la page) prioritaires
      if (forcedFilters) command.filters = { ...command.filters, ...forcedFilters };
      const result = admin
        ? await courtiersAPI.getAdminCourtiers({ getGenericListCommandDto: command })
        : await courtiersAPI.getCourtiers({ getGenericListCommandDto: command });
      const items = result.items || [];
      return {
        items,
        totalItems: result.totalCount || items.length,
        totalPages: Math.ceil((result.totalCount || items.length) / params.pageSize),
        currentPage: params.pageNumber
      };
    },
    [admin, forcedFilters]
  );

  const fetchCourtierFacets = useCallback(
    async (command: FacetQueryCommand) => {
      // Les facettes sont calculées dans le périmètre imposé (ex. la plateforme courante)
      const scoped = forcedFilters
        ? { ...command, filters: { ...command.filters, ...forcedFilters } }
        : command;
      const res = admin
        ? await courtiersAPI.getAdminCourtierListFacets({ getGenericListCommandDto: scoped })
        : await courtiersAPI.getCourtierListFacets({ getGenericListCommandDto: scoped });
      // Masque les facettes non pertinentes ici (ex. « plateformes » sur une page d'orga)
      if (hiddenFacetKeys?.length) {
        return { ...res, facets: res.facets.filter((f) => !hiddenFacetKeys.includes(f.key)) };
      }
      return res;
    },
    [admin, forcedFilters, hiddenFacetKeys]
  );

  const table = (
    <ServerTable<CourtierListItem>
      apiConfig={{ fetchData: fetchCourtiers }}
      columns={columns}
      title={
        embedded
          ? titleLabel
            ? { iconKey: 'building', label: titleLabel, description: titleDescription }
            : undefined
          : admin
            ? undefined // le titre est porté par le header d'identité admin
            : { iconKey: 'building', label: 'Courtiers' }
      }
      selectable={!embedded}
      serverSorting
      centerColumns
      refreshTrigger={refreshTrigger}
      columnCustomization={{
        storageKey: storageKey ?? (admin ? 'bo-courtiers' : 'company-courtiers'),
        defaultHidden: defaultHiddenColumns
      }}
      facets={{
        fetchFacets: fetchCourtierFacets,
        // « Inclure tests » : liste admin uniquement (non applicable sur /courtiers/liste).
        booleanFacets: admin ? INCLUDE_TESTS_BOOLEAN_FACET : undefined
      }}
      onExport={
        admin && !embedded
          ? (p) =>
              exportListCsv(
                '/api/backoffice/courtiers/admin/liste/csv',
                buildExportCommand(p, mapFacetFiltersToFiltersDto(p.filters))
              )
          : undefined
      }
      actions={
        createConfig || rowActions
          ? { ...(createConfig && { create: createConfig }), ...(rowActions && { row: rowActions }) }
          : undefined
      }
    />
  );

  // Embarqué (ex. détail d'une plateforme) : pas de layout dashboard autour
  if (embedded) return table;

  return (
    <SimpleDashboardContent>
      {/* Barre de recherche globale : espace courtier uniquement, pas en admin BO */}
      {!admin && <DashboardHeader />}
      {/* Identité de page admin (le titre du tableau est porté par ce header) */}
      {admin && <GenericViewHeader title="Courtiers" icon={Building2} section="Acteurs" />}
      {table}
    </SimpleDashboardContent>
  );
};

export default CourtiersView;
